import type {
  ArtifactManifest,
  DrawingDocument,
  ResolvedDocument,
  QualityIssueKind,
} from '../contract/types.js';
import { writeDxf, canonicalDxf, type WriteDxfOptions } from '../dxf/writer.js';
import { checkGeometricValidity, type ValidityFailure } from '../dxf/validity.js';
import { renderOverlay } from '../overlay/svg.js';
import { toDrawingSpace } from '../transform/space.js';
import { sha256Hex } from './hash.js';
import { EXTRACTOR_BUNDLE_VERSION } from '../extract/pdf.js';

/**
 * The tuning profile version. A change to matching or recognition changes this.
 *
 * 0.2.0 — text reassembly requires two lines to be **written consecutively** in
 * the content stream before it will group them into one block. Bumped because
 * the gate scores reassembly: this changes which entities a drawing produces, so
 * it has to be a reviewable artefact rather than a silent shift. See
 * `docs/findings/09-text-object-boundaries-are-a-producer-choice.md`.
 */
export const PROFILE_VERSION = '0.2.0';

export interface Artefacts {
  json: string;
  /**
   * Null when physical scale could not be determined. A DXF at the wrong scale
   * is worse than no DXF, because it looks correct.
   */
  dxf: string | null;
  /** One overlay per page, in page order. */
  overlays: string[];
  metrics: string;
  manifests: ArtifactManifest[];
}

/**
 * A type guard rather than a cast.
 *
 * The original code wrote `doc as ResolvedDocument` behind a runtime check,
 * which type-checks but throws away the guarantee the type exists to give: a
 * later edit could move the cast away from its guard and nothing would
 * complain. A guard narrows honestly and cannot drift from its condition.
 */
export function isScaleResolved(doc: DrawingDocument): doc is ResolvedDocument {
  return (
    doc.scaleState === 'resolved' &&
    typeof doc.mmPerPoint === 'number' &&
    doc.mmPerPoint > 0 &&
    doc.pages.every((p) => p.transform.length === 6)
  );
}

export interface Metrics {
  contractVersion: string;
  profileVersion: string;
  extractorBundleVersion: string;
  scaleState: 'resolved' | 'unknown';
  unit: string;
  dxfRefused: boolean;
  dxfRefusedReason: string | null;
  pageCount: number;
  entityCount: number;
  entitiesByType: Record<string, number>;
  qualityIssueTotal: number;
  qualityIssuesByKind: Record<string, number>;
  geometricValidityFailures: ValidityFailure[];
  ledger: DrawingDocument['ledger'];
  /** The headline number: operators that neither became entities nor were recorded. */
  unaccounted: number;
}

/**
 * Produces the four artefacts.
 *
 * Three of them are always produced. The DXF is produced only when physical
 * scale is known — which is not a check here but a property of the type: a
 * document at unknown scale cannot be passed to the writer at all.
 */
export async function buildArtefacts(
  doc: DrawingDocument,
  options: WriteDxfOptions = {},
): Promise<Artefacts> {
  const json = JSON.stringify(doc, null, 2);

  // One overlay per page. Rendering only the first silently loses every later
  // sheet, and a reviewer checking a two-page drawing would never know.
  const overlays = doc.pages.map((page) => renderOverlay(doc, page.index));

  const resolved = isScaleResolved(doc);
  // The text style reaches the DXF and nothing else. It cannot change the JSON,
  // the overlays or the metrics, so choosing a font cannot move an accuracy figure.
  const dxf = resolved ? writeDxf(doc, options) : null;

  const metrics = JSON.stringify(computeMetrics(doc), null, 2);

  const single = doc.pages.length <= 1;
  const parts = [
    { key: 'drawing.json', type: 'application/json', body: json, hashed: json },
    ...overlays.map((svg, i) => ({
      key: single ? 'overlay.svg' : `overlay.p${i + 1}.svg`,
      type: 'image/svg+xml',
      body: svg,
      hashed: svg,
    })),
    { key: 'metrics.json', type: 'application/json', body: metrics, hashed: metrics },
  ];
  if (dxf !== null) {
    parts.push({ key: 'drawing.dxf', type: 'image/vnd.dxf', body: dxf, hashed: canonicalDxf(dxf) });
  }

  const manifests: ArtifactManifest[] = [];
  for (const p of parts) {
    manifests.push({
      contentType: p.type,
      sha256: await sha256Hex(p.hashed),
      bytes: new TextEncoder().encode(p.body).byteLength,
      storageKey: p.key,
      schemaVersion: doc.contractVersion,
      profileVersion: PROFILE_VERSION,
      extractorBundleVersion: EXTRACTOR_BUNDLE_VERSION,
    });
  }

  return { json, dxf, overlays, metrics, manifests };
}

export function computeMetrics(doc: DrawingDocument): Metrics {
  const entities = doc.pages.flatMap((p) => p.entities);
  const resolved = isScaleResolved(doc);

  // Validity is checked on the geometry as it will be written, not as it is
  // held, because that is what a CAD application will read — and using each
  // page's own transform, for the same reason the writer does.
  const written = resolved
    ? doc.pages.flatMap((p) => p.entities.map((e) => toDrawingSpace(e, p.transform)))
    : entities;

  const entitiesByType: Record<string, number> = {};
  for (const e of entities) entitiesByType[e.type] = (entitiesByType[e.type] ?? 0) + 1;

  const qualityIssuesByKind: Record<string, number> = {};
  for (const q of doc.qualityIssues) {
    const kind: QualityIssueKind = q.kind;
    qualityIssuesByKind[kind] = (qualityIssuesByKind[kind] ?? 0) + q.count;
  }

  return {
    contractVersion: doc.contractVersion,
    profileVersion: PROFILE_VERSION,
    extractorBundleVersion: doc.provenance.extractorBundleVersion,
    scaleState: doc.scaleState,
    unit: doc.unit,
    dxfRefused: !resolved,
    dxfRefusedReason: resolved
      ? null
      : 'physical scale could not be determined; a DXF at the wrong scale is worse than no DXF',
    pageCount: doc.pages.length,
    entityCount: entities.length,
    entitiesByType,
    qualityIssueTotal: doc.qualityIssues.reduce((n, q) => n + q.count, 0),
    qualityIssuesByKind,
    geometricValidityFailures: checkGeometricValidity(written),
    ledger: doc.ledger,
    // The operators nothing could classify. It used to be
    // `total - consumed - recorded`, where `total` was *defined* as
    // `consumed + recorded` — an expression compared with itself, exactly zero
    // on every drawing ever converted, and the reason the gate's rule for a
    // non-zero value had never been able to fire.
    unaccounted: doc.ledger.unclassified,
  };
}
